import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, RotateCcw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import MenuDropzone from "@/components/menu/MenuDropzone";
import AnalyzingSteps from "@/components/menu/AnalyzingSteps";
import { StatTiles, ObservationCard, MarginNote, catLine } from "@/components/menu/MenuInsights";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { track, isInternal } from "@/lib/analytics";
import { getPending, setPending } from "@/lib/menuFunnel";
import { t } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export async function runAnalysis(file) {
  track("menu_upload_started", { file_type: file.type || file.name.split(".").pop() });
  const fd = new FormData();
  fd.append("file", file);
  fd.append("internal", isInternal() ? "true" : "false");
  let sent = false;
  let data;
  try {
    ({ data } = await api.post("/menu-analysis", fd, {
      timeout: 120000,
      onUploadProgress: (e) => {
        if (!sent && e.total && e.loaded >= e.total) { sent = true; track("menu_upload_completed"); }
      },
    }));
  } catch (e) {
    track("menu_analysis_failed", { status: e.response?.status || 0 });
    throw e;
  }
  if (!sent) track("menu_upload_completed");
  track("menu_analysis_completed", { dish_count: data.stats.dish_count, category_count: data.stats.category_count });
  return data;
}

function Preview({ a, onRestart }) {
  return (
    <div className="space-y-6" data-testid="menu-preview">
      <div className="fade-up">
        <p className="eyebrow">{t("menuAnalyzer.apercu_de_votre_analyse")}</p>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight sm:text-4xl" data-testid="menu-preview-title">{a.restaurant_name || t("common.votre_carte")} : {a.stats.dish_count} {t("common.plats_detectes")}</h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base" data-testid="menu-preview-categories">{catLine(a.stats.categories)}</p>
        {a.summary && <p className="mt-3 max-w-3xl text-sm leading-relaxed sm:text-base">{a.summary}</p>}
      </div>
      <StatTiles stats={a.stats} />
      {a.observations.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-bold md:text-lg">{t("menuAnalyzer.premieres_observations")}</h2>
          {a.observations.map((o, i) => <ObservationCard key={i} o={o} i={i} />)}
        </div>
      )}
      <MarginNote />
      <div className="relative overflow-hidden rounded-2xl border bg-card p-5 sm:p-8" data-testid="menu-locked-section">
        <div className="relative flex flex-col items-center p-2 text-center">
          <div aria-hidden className="pointer-events-none absolute inset-0 select-none space-y-3 blur-[6px]">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-muted/70" />)}
          </div>
          <div className="absolute inset-0 bg-background/50" />
          <div className="relative flex w-full flex-col items-center">
          <div className="mb-3 rounded-xl bg-emerald-500/15 p-3"><Lock className="h-5 w-5 text-emerald-500" /></div>
          <p className="text-lg font-bold sm:text-xl">{t("menuAnalyzer.votre_analyse_complete_est_prete")}</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            {a.locked_observations > 0 && t("menuAnalyzer.x_autres_observations", { v0: a.locked_observations })}{t("menuAnalyzer.detail_des")} {a.stats.category_count} {t("menuAnalyzer.rubriques_et_repartition_des_prix")}
          </p>
          <Button size="lg" asChild className="mt-4 h-auto min-h-12 w-full max-w-sm whitespace-normal py-3 text-base leading-tight" data-testid="menu-unlock-btn">
            <Link to="/register">{t("menuAnalyzer.voir_mon_analyse_complete_gratuitement")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Link to="/login" className="mt-3 text-xs text-muted-foreground underline-offset-4 hover:underline" data-testid="menu-unlock-login-link">{t("menuAnalyzer.j_ai_deja_un_compte")}</Link>
          </div>
        </div>
      </div>
      <button type="button" onClick={onRestart} className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="menu-restart-btn"><RotateCcw className="h-3 w-3" />{t("menuAnalyzer.analyser_une_autre_carte")}</button>
    </div>
  );
}

export default function MenuAnalyzer() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("upload");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const restored = useRef(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const p = getPending();
    if (!p) return;
    api.get(`/menu-analysis/${p.id}/preview`, { params: { token: p.token } })
      .then(({ data }) => { setResult(data); setState("preview"); })
      .catch(() => {});
  }, []);

  const onFile = async (f) => {
    setFile(f);
    setError("");
    setState("analyzing");
    try {
      const data = await runAnalysis(f);
      setPending(data.id, data.token);
      if (user) return nav("/ma-carte?claim=1");
      setResult(data);
      setState("preview");
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(errMsg(e));
      setState("upload");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-4xl items-center justify-between gap-2 px-4 py-5 sm:px-6">
        <Link to="/" data-testid="menu-home-link"><Logo /></Link>
        <div className="flex items-center gap-2"><LanguageSwitcher />
        {!user && <Button variant="ghost" size="sm" asChild data-testid="menu-login-btn"><Link to="/login">{t("common.se_connecter")}</Link></Button>}</div>
      </header>
      <main className="mx-auto max-w-4xl px-4 pb-16 pt-2 sm:px-6 sm:pt-8">
        {state === "upload" && (
          <div className="space-y-5 fade-up">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl">{t("menuAnalyzer.analysez_votre_carte_gratuitement")}</h1>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">{t("menuAnalyzer.sans_compte_sans_saisie_resultat_en_moins")}</p>
            </div>
            {error && <p className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-600 dark:text-rose-400" data-testid="menu-error"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
            <MenuDropzone onFile={onFile} />
            <p className="text-center text-xs text-muted-foreground">{t("common.photo_ou_pdf_analyse_rapide_sans_saisie_ma")}</p>
          </div>
        )}
        {state === "analyzing" && <div className="pt-6 sm:pt-12"><AnalyzingSteps fileName={file?.name} /></div>}
        {state === "preview" && result && <Preview a={result} onRestart={() => { setResult(null); setState("upload"); }} />}
      </main>
    </div>
  );
}
